import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import ArrowBackOutlinedIcon from "@mui/icons-material/ArrowBackOutlined";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import PageHeader from "../../components/common/PageHeader";
import PageLoader from "../../components/common/PageLoader";
import StatusChip from "../../components/common/StatusChip";
import CreateTenant from "../../components/CreateTenant";
import { tenantmgmtService } from "../../services";
import { appActions, userActions } from "../../redux/actions";
import { getApiErrorMessage } from "../../helpers";
import { normalizeTenant } from "../../utils/normalizeTenant";

const DetailItem = ({ label, value }) => (
  <Box>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography
      variant="body2"
      sx={{ fontWeight: 600, mt: 0.25, wordBreak: "break-word" }}
    >
      {value || "—"}
    </Typography>
  </Box>
);

const TenantDetails = () => {
  const { tenantId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const listPath = "/tenantmgmt";

  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadTenant = useCallback(async () => {
    const decoded = decodeURIComponent(tenantId || "");
    if (!decoded) return;
    setLoading(true);
    setError("");
    try {
      const fromState = location.state?.tenant || null;
      let detail = null;
      try {
        detail = await tenantmgmtService.getTenantById(decoded);
      } catch {
        detail = null;
      }
      const seed =
        fromState && String(fromState.id) === String(decoded) ? fromState : null;
      if (!detail && !seed) {
        throw new Error("Tenant not found.");
      }
      setTenant(normalizeTenant({ ...(seed || {}), ...(detail || {}) }));
    } catch (err) {
      setTenant(null);
      setError(getApiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [location.state, tenantId]);

  useEffect(() => {
    loadTenant();
  }, [loadTenant]);

  const handleOpenTenant = () => {
    const tenantKey = tenant?.name || tenant?.slug;
    if (!tenantKey) return;
    dispatch(
      userActions.setActiveTenant({
        id: tenantKey,
        name: tenant.name || tenantKey,
      })
    );
    navigate("/tenant-clients");
  };

  const handleEditSuccess = async () => {
    setEditOpen(false);
    await loadTenant();
  };

  const handleDelete = async () => {
    const id = tenant?.id;
    if (!id) {
      dispatch(appActions.apiErrorMessage("Tenant ID is missing."));
      return;
    }
    setSaving(true);
    try {
      await tenantmgmtService.deleteTenant(id);
      dispatch(appActions.apiSuccessMessage("Tenant deleted successfully."));
      setDeleteOpen(false);
      navigate(listPath);
    } catch (err) {
      dispatch(
        appActions.apiErrorMessage(
          getApiErrorMessage(err) || "Failed to delete tenant."
        )
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <PageLoader text="Loading tenant..." />;
  }

  if (error && !tenant) {
    return (
      <Box>
        <Button
          startIcon={<ArrowBackOutlinedIcon />}
          onClick={() => navigate(listPath)}
        >
          Back to tenants
        </Button>
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      </Box>
    );
  }

  if (!tenant) return null;

  return (
    <Box sx={{ width: "100%" }}>
      <Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
        <Button
          startIcon={<ArrowBackOutlinedIcon />}
          onClick={() => navigate(listPath)}
        >
          Back to tenants
        </Button>
      </Stack>
      <PageHeader
        title="Tenant Details"
        subtitle={tenant.name || tenant.id}
      />

      <Paper
        elevation={0}
        sx={{ p: 3, border: "1px solid", borderColor: "divider", mb: 2 }}
      >
        <Stack spacing={3}>
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
              Basic Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <DetailItem label="Tenant ID" value={tenant.id} />
              </Grid>
              <Grid item xs={12} md={6}>
                <DetailItem label="Name" value={tenant.name} />
              </Grid>
              <Grid item xs={12} md={6}>
                <DetailItem label="Contact Person" value={tenant.contactPerson} />
              </Grid>
              <Grid item xs={12} md={6}>
                <DetailItem label="Email" value={tenant.email} />
              </Grid>
              <Grid item xs={12} md={6}>
                <DetailItem label="Mobile" value={tenant.mobile} />
              </Grid>
              {tenant.dns ? (
                <Grid item xs={12} md={6}>
                  <DetailItem label="DNS" value={tenant.dns} />
                </Grid>
              ) : null}
            </Grid>
          </Box>

          <Divider />

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
              Status
            </Typography>
            <StatusChip enabled={tenant.enabled} />
          </Box>
        </Stack>
      </Paper>

      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1.25}
        flexWrap="wrap"
        useFlexGap
      >
        <Button variant="contained" onClick={() => setEditOpen(true)}>
          Edit Tenant
        </Button>
        <Button variant="outlined" onClick={handleOpenTenant}>
          Open tenant
        </Button>
        <Button
          variant="outlined"
          color="error"
          onClick={() => setDeleteOpen(true)}
        >
          Delete Tenant
        </Button>
      </Stack>

      <CreateTenant
        open={editOpen}
        onClose={() => setEditOpen(false)}
        tenantData={tenant}
        onSuccess={handleEditSuccess}
      />

      <Dialog
        open={deleteOpen}
        onClose={saving ? undefined : () => setDeleteOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Delete Tenant</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Delete tenant <strong>{tenant.name || tenant.id}</strong>? This
            cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDelete}
            disabled={saving}
          >
            {saving ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TenantDetails;
